import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Ticket, Loader2, TimerReset } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCountdown, pad, sectionFor } from "@/lib/offer";
import { errText } from "@/lib/aadhaar";
import { CouponShareButton } from "@/components/admission/CouponShare";

export const useCouponStats = () => {
  const [stats, setStats] = useState(null);
  const load = useCallback(() => api.get("/coupons/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  return [stats, load];
};

export const CouponCounters = ({ stats, active }) => {
  const { L } = useLang();
  if (!stats) return null;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="coupon-counters">
      {Object.values(stats.sections).map((s) => (
        <div key={s.section} className={`rounded-xl border p-2.5 bg-white transition-[border-color] ${active === s.section ? "border-[#FF2E63]" : "border-slate-200"}`} data-testid={`coupon-counter-${s.section}`}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{L("Section", "सेक्शन")} {s.section} · {s.classes.join(", ")}</div>
          <div className="text-lg font-black text-[#0A192F] font-['Outfit'] tabular-nums" data-testid={`coupon-remaining-${s.section}`}>{s.remaining}<span className="text-xs text-slate-400 font-bold">/{s.cap}</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#FF3B3B] transition-[width] duration-700" style={{ width: `${(s.remaining / s.cap) * 100}%` }} /></div>
          <div className="text-[10px] text-slate-500 mt-1">{L("left", "बाकी")} · {s.used} {L("used", "उपयोग")}</div>
        </div>
      ))}
    </div>
  );
};

const Timer = ({ until, onEnd }) => {
  const { L } = useLang();
  const t = useCountdown(until);
  useEffect(() => { if (t.ms === 0) onEnd(); }, [t.ms, onEnd]);
  return <span className={`inline-flex items-center gap-1 text-sm font-bold tabular-nums ${t.ms < 600000 ? "text-[#FF3B3B]" : "text-[#007A8A]"}`} data-testid="coupon-timer"><TimerReset className="w-4 h-4" />{L("Expires in", "समाप्ति")} {pad(t.h * 60 + t.m)}:{pad(t.s)}</span>;
};

export const CodeBox = ({ coupon, onExpire }) => {
  const { L } = useLang();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(coupon.code); } catch { /* clipboard blocked: code stays visible */ }
    setCopied(true); toast.success(L("Coupon copied", "कूपन कॉपी हुआ")); setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="rounded-2xl bg-[#0A192F] p-4 sm:p-5 text-white" data-testid="coupon-code-box">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FFD166]">{coupon.discount_pct}% {L("off · one-time use", "छूट · एक बार उपयोग")}</span>
        <Timer until={coupon.expires_at} onEnd={onExpire} />
      </div>
      <button type="button" onClick={copy} className="mt-3 w-full flex items-center justify-between gap-3 rounded-xl border-2 border-dashed border-[#FFD166] bg-[#FFD166]/10 px-4 py-4 hover:bg-[#FFD166]/20 active:scale-[0.99] transition-[background-color,transform]" data-testid="coupon-copy-btn">
        <span className="text-2xl sm:text-4xl font-black tracking-wide sm:tracking-wider whitespace-nowrap font-['Outfit'] text-[#FFD166] select-all" data-testid="coupon-code">{coupon.code}</span>
        <span className="shrink-0 inline-flex items-center gap-1 text-sm font-bold bg-[#FFD166] text-[#0A192F] rounded-full px-3 py-1.5">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? L("Copied", "कॉपी") : L("Copy", "कॉपी")}</span>
      </button>
      <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
        <span className="text-xs text-slate-300">{L("Tell friends! They can grab their own coupon.", "दोस्तों को बताएँ! वे भी अपना कूपन ले सकते हैं।")}</span>
        <CouponShareButton pct={coupon.discount_pct} refCode={coupon.ref_code} />
      </div>
    </div>
  );
};

export const CouponBox = ({ form, coupon, setCoupon, onStatsChange }) => {
  const { L } = useLang();
  const [busy, setBusy] = useState(false);
  const ready = form.email && form.phone && form.class_grade;
  const get = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/issue", { email: form.email, phone: form.phone, class_grade: form.class_grade, student_name: form.student_name });
      setCoupon(data); onStatsChange();
      toast.success(data.reused ? L("Your live coupon is shown again", "आपका चालू कूपन फिर दिखाया गया") : L(`You won ${data.discount_pct}% off!`, `आपको ${data.discount_pct}% छूट मिली!`));
    } catch (e) { toast.error(errText(e.response?.data?.detail, L("Could not get a coupon", "कूपन नहीं मिल सका"))); }
    finally { setBusy(false); }
  };
  if (coupon) return <CodeBox coupon={coupon} onExpire={() => { setCoupon(null); toast.error(L("Coupon expired. Get a new one.", "कूपन समाप्त। नया लें।")); }} />;
  return (
    <div className="rounded-2xl border-2 border-dashed border-[#FF2E63]/50 bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="coupon-get-panel">
      <div className="flex gap-3 items-start">
        <Ticket className="w-6 h-6 text-[#FF2E63] shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-[#0A192F]">{L("Get my coupon: 50% or 70% off", "मेरा कूपन लें: 50% या 70% छूट")}</div>
          <div className="text-xs text-slate-500">{ready ? L(`Section ${sectionFor(form.class_grade)} · valid for 1 hour · new admissions only`, `सेक्शन ${sectionFor(form.class_grade)} · 1 घंटे के लिए मान्य · केवल नए दाखिले`) : L("Fill email, phone and class first", "पहले ईमेल, फ़ोन और कक्षा भरें")}</div>
        </div>
      </div>
      <Button type="button" onClick={get} disabled={!ready || busy} className="rounded-full rs-cta shrink-0" data-testid="coupon-get-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Get my coupon", "कूपन लें")}</Button>
    </div>
  );
};
